/**
 * 138 (research.md R3): the two platform predicates the PWA surfaces share. Both were private to
 * `PushNotificationsSettings.tsx` until the install offer needed the same rules; extracted rather
 * than duplicated, so a change to either lands in one place (Constitution VI).
 *
 * 123 FR-015/SC-007 (research.md R9): iOS Safari only supports push for an app installed to the
 * home screen. `navigator.platform === 'MacIntel' && maxTouchPoints > 1` catches iPadOS 13+,
 * which reports a desktop Mac user agent by default.
 */
export function isIOS(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/**
 * True when the app is already running as an installed app rather than in a browser tab.
 *
 * 138: the `matchMedia` guard is new. Its absence was harmless while this only ran from a user
 * gesture in a real browser, but the install offer evaluates it on every mount — including in
 * jsdom, which does not implement `matchMedia` at all. `PickerPanel.tsx:76` guards for the same
 * reason. `navigator.standalone` stays non-standard and iOS-only, hence the local cast.
 */
export function isStandaloneDisplay(): boolean {
  return (
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * 206 (#285, research.md R4): the iOS Home-Screen-install precondition message, shared between
 * `PushNotificationsSettings.tsx`'s "Enable" button and `PushOptInBanner.tsx` — one definition
 * (Constitution VI) rather than two copies of the same sentence drifting apart.
 */
export const IOS_PUSH_INSTALL_HINT =
  'Add this app to your Home Screen first (Share → Add to Home Screen), then enable notifications from there.';
